import { TranslationFunction } from '@/src/types/translations'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Divider, Typography } from '@mui/material'
import React from 'react'

const parseTextWithLinks = (text: string): React.ReactNode => {
    // First, check if the text contains HTML anchor tags
    if (text.includes('<a href=')) {
        // Remove the anchor tags and just return the text with proper links
        const cleanedText = text.replace(/<a\s+[^>]*href=['"]([^'"]+)['"][^>]*>([^<]*)<\/a>/gi, (match, url) => {
            return url
        })
        
        // Now parse the cleaned text for URLs
        const urlRegex = /(https?:\/\/[^\s]+)/g
        const parts = cleanedText.split(urlRegex)
        
        return parts.map((part, index) => {
            if (part.match(urlRegex)) {
                return (
                    <a 
                        key={index} 
                        href={part} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#0066CC', textDecoration: 'underline' }}
                    >
                        {part}
                    </a>
                )
            }
            return part
        })
    }
    
    // If no HTML anchor tags, parse for plain URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g
    const parts = text.split(urlRegex)
    
    return parts.map((part, index) => {
        if (part.match(urlRegex)) {
            return (
                <a 
                    key={index} 
                    href={part} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: '#0066CC', textDecoration: 'underline' }}
                >
                    {part}
                </a>
            )
        }
        return part
    })
}

export interface Note {
    title: string
    content: string
}

interface ImportantInformationBoxProps {
    notes: Note[]
    t: TranslationFunction<'common'>
}

const ImportantInformationBox: React.FC<ImportantInformationBoxProps> = ({ notes, t }) => (
    <Box
        sx={{
            mt: 2,
            width: '100%',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'borders.default',
            borderRadius: 2,
            px: 2.5,
            py: 2,
            mx: 'auto'
        }}
    >
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: 'grey.900', letterSpacing: -0.5, fontSize: 19 }}>
            {t('importantInformation')}
        </Typography>
        {notes.map((note, idx) => (
            <Box key={idx} sx={{ mb: idx < notes.length - 1 ? 2 : 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.7 }}>
                    <InfoOutlinedIcon sx={{ color: 'info.main', fontSize: 18, mr: 0.7 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'grey.900', fontSize: 14 }}>
                        {note.title}
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'grey.800', lineHeight: 1.7, fontSize: 13, ml: 2.8 }}>
                    {parseTextWithLinks(note.content)}
                </Typography>
                {idx < notes.length - 1 && <Divider sx={{ my: 2, borderColor: 'grey.300' }} />}
            </Box>
        ))}
    </Box>
)

export default ImportantInformationBox
